// ─────────────────────────────────────────────────────────────────────────────
// Button.jsx — MIAVIT style v2
// Sharp corners, DM Sans, green/white patterns matching current site style
//
// Variants:
//   'green'         → green filled, white text  (main CTA)
//   'white'         → white filled, green text  (on green bg)
//   'outline-green' → transparent, green border + text → fills green on hover
//   'outline-white' → transparent, white border + text → slight white fill on hover
//   'amber'         → amber filled, white text  (accent CTA)
//
// Sizes:  'sm' | 'md' | 'lg'
//
// Usage:
//   <Button to="/contact">Contact Us</Button>
//   <Button variant="white" to="/about">Learn More</Button>
//   <Button variant="outline-green" href="https://miavit.com">Visit Site</Button>
//   <Button variant="outline-white" onClick={fn}>Get In Touch</Button>
//   <Button variant="amber" size="lg" to="/products">Explore Products</Button>
//   <Button loading>Sending...</Button>
//   <Button disabled>Unavailable</Button>
// ─────────────────────────────────────────────────────────────────────────────

import { Link } from 'react-router-dom'

// ── Loading spinner ───────────────────────────────────────────────────────────
const Spinner = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
    style={{ animation: 'btn-spin 0.7s linear infinite', flexShrink: 0 }}>
    <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.3"/>
    <path d="M7 1.5A5.5 5.5 0 0112.5 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
)

// ── Variant definitions ───────────────────────────────────────────────────────
const VARIANTS = {
  // Green filled → white text (primary action)
  green: {
    bg:         '#007935',
    color:      '#ffffff',
    border:     'none',
    hoverBg:    '#065e32',
    hoverColor: '#ffffff',
    hoverBorder:'none',
  },
  // White filled → green text (on green backgrounds)
  white: {
    bg:         '#ffffff',
    color:      '#007935',
    border:     '1.5px solid rgba(255,255,255,0.75)',
    hoverBg:    'rgba(255,255,255,0.88)',
    hoverColor: '#007935',
    hoverBorder:'1.5px solid rgba(255,255,255,0.75)',
  },
  // Outline green (transparent → fills on hover)
  'outline-green': {
    bg:         'transparent',
    color:      '#007935',
    border:     '1.5px solid #007935',
    hoverBg:    '#007935',
    hoverColor: '#ffffff',
    hoverBorder:'1.5px solid #007935',
  },
  // Outline white (on green/dark backgrounds)
  'outline-white': {
    bg:         'transparent',
    color:      '#ffffff',
    border:     '1.5px solid rgba(255,255,255,0.75)',
    hoverBg:    'rgba(255,255,255,0.15)',
    hoverColor: '#ffffff',
    hoverBorder:'1.5px solid rgba(255,255,255,0.9)',
  },
  // Amber filled (accent)
  amber: {
    bg:         '#ed9716',
    color:      '#ffffff',
    border:     'none',
    hoverBg:    '#cc7510',
    hoverColor: '#ffffff',
    hoverBorder:'none',
  },
}

// ── Size definitions ──────────────────────────────────────────────────────────
const SIZES = {
  sm: { padding: '6px 14px',  fontSize: '12.5px' },
  md: { padding: '8px 18px',  fontSize: '13px'   },
  lg: { padding: '10px 22px', fontSize: '14px'   },
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function Button({
  children,
  variant   = 'green',
  size      = 'md',
  to,              // React Router internal link
  href,            // External link
  onClick,
  loading   = false,
  disabled  = false,
  fullWidth = false,
  showArrow = false,  // false by default — MIAVIT buttons don't show arrows
  style: extraStyle = {},
  className = '',
  ...props
}) {
  const v = VARIANTS[variant] || VARIANTS.green
  const s = SIZES[size]       || SIZES.md

  const baseStyle = {
    // Base
    display:        'inline-flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            '8px',
    fontFamily:     "'DM Sans', sans-serif",
    fontWeight:     600,
    letterSpacing:  '0.01em',
    textDecoration: 'none',
    whiteSpace:     'nowrap',
    cursor:         disabled ? 'not-allowed' : 'pointer',
    lineHeight:     1,
    // No border-radius — MIAVIT sharp corners
    borderRadius:   0,
    // Variant
    background:     v.bg,
    color:          v.color,
    border:         v.border || 'none',
    // Size
    ...s,
    // States
    opacity:        disabled ? 0.5 : 1,
    width:          fullWidth ? '100%' : 'auto',
    transition:     'background 0.2s ease, color 0.2s ease, border-color 0.2s ease',
    // Overrides
    ...extraStyle,
  }

  const handleEnter = (e) => {
    if (disabled || loading) return
    e.currentTarget.style.background = v.hoverBg
    e.currentTarget.style.color      = v.hoverColor
    if (v.border) e.currentTarget.style.border = v.hoverBorder
  }

  const handleLeave = (e) => {
    e.currentTarget.style.background = v.bg
    e.currentTarget.style.color      = v.color
    if (v.border) e.currentTarget.style.border = v.border
  }

  const content = (
    <>
      {loading && <Spinner />}
      <span>{loading ? 'Loading...' : children}</span>
      {showArrow && !loading && (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <path d="M2 6.5h9M6.5 3L9.5 6.5 6.5 10"
            stroke="currentColor" strokeWidth="1.75"
            strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </>
  )

  const sharedProps = {
    style:        baseStyle,
    onMouseEnter: handleEnter,
    onMouseLeave: handleLeave,
    className,
    ...props,
  }

  if (to) return <Link to={to} {...sharedProps}>{content}</Link>
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" {...sharedProps}>{content}</a>
  return <button onClick={onClick} disabled={disabled || loading} {...sharedProps}>{content}</button>
}

// Inject spinner keyframe once
if (typeof document !== 'undefined' && !document.getElementById('btn-spin-kf')) {
  const s = document.createElement('style')
  s.id = 'btn-spin-kf'
  s.textContent = '@keyframes btn-spin { to { transform: rotate(360deg); } }'
  document.head.appendChild(s)
}